// src/components/HealthView.js

import React, { useEffect, useState } from "react";
import { getProviderHealth, getProviderHealthHistory } from "../api/health";
import { getProviderFundamentals, getProviderNews } from "../api/providers";
import ResponsiveContainer from "./ResponsiveContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stack,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HealthTrendIcon from "./HealthTrendIcon";

// Track previous health to show trend
const statusRank = { ok: 2, warn: 1, error: 0 };

const PROVIDER_ROLES = {
  alpaca:       "Broker & portfolio sync",
  polygon:      "Real-time market prices",
  twelvedata:   "Price data backup",
  fmp:          "Company fundamentals",
  finnhub:      "Quotes & company data",
  alphavantage: "Price history & indicators",
  fred:         "Macroeconomic data",
  yfinance:     "Price fallback (free)",
  finviz:       "Supplemental data",
  binance:      "Crypto prices",
};

function isCircuitOpenRow(row) {
  const errorType = String(row?.error_type || "").toLowerCase();
  const message = String(row?.message || "").toLowerCase();
  return errorType === "circuit_open" || message.includes("circuit open");
}

const HealthView = () => {
  const [providerHealth, setProviderHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [prevHealth, setPrevHealth] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [rowLoading, setRowLoading] = useState({});
  const [inspectTicker, setInspectTicker] = useState("AAPL");
  const [inspectLoading, setInspectLoading] = useState(false);
  const [inspectError, setInspectError] = useState("");
  const [inspectFundamentals, setInspectFundamentals] = useState(null);
  const [inspectNews, setInspectNews] = useState([]);
  const [inspectErrors, setInspectErrors] = useState([]);

  const fetchHealth = (force = false) => {
    setLoading(true);
    setError("");
    Promise.all([getProviderHealth(force), getProviderHealthHistory(10)])
      .then(([data, history]) => {
        setPrevHealth(providerHealth || {});
        setProviderHealth(data);
        setHealthHistory(Array.isArray(history) ? history : []);
      })
      .catch(() => setError("Unable to load health data. Please check your connection or try again."))
      .finally(() => setLoading(false));
  };

  // Per-provider refresh
  const handleProviderRefresh = async (provider) => {
    setRowLoading((prev) => ({ ...prev, [provider]: true }));
    try {
      // Always force refresh for per-provider
      const [allHealth, history] = await Promise.all([
        getProviderHealth(true),
        getProviderHealthHistory(10),
      ]);
      setPrevHealth((prev) => ({ ...prev, [provider]: providerHealth?.[provider] }));
      setProviderHealth((prev) => ({ ...prev, [provider]: allHealth[provider] }));
      setHealthHistory(Array.isArray(history) ? history : []);
    } catch {
      setError(`Unable to refresh health for ${provider}`);
    } finally {
      setRowLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line
  }, [retryCount]);

  const handleRefresh = () => {
    fetchHealth(true);
  };

  const handleInspectProviderData = async (event) => {
    event.preventDefault();
    const symbol = String(inspectTicker || "").trim().toUpperCase();
    if (!symbol) {
      setInspectError("Enter a ticker symbol.");
      return;
    }

    setInspectLoading(true);
    setInspectError("");
    setInspectFundamentals(null);
    setInspectNews([]);
    setInspectErrors([]);

    try {
      const [fundPayload, newsPayload] = await Promise.all([
        getProviderFundamentals(symbol).catch(() => null),
        getProviderNews(symbol).catch(() => null),
      ]);

      if (fundPayload?.fundamentals) {
        setInspectFundamentals(fundPayload);
      }
      if (Array.isArray(newsPayload?.news)) {
        setInspectNews(newsPayload.news.slice(0, 10));
      }

      const allErrors = [];
      if (Array.isArray(fundPayload?.errors)) {
        allErrors.push(...fundPayload.errors);
      }
      if (Array.isArray(newsPayload?.errors)) {
        allErrors.push(...newsPayload.errors);
      }
      setInspectErrors(allErrors);

      if (!fundPayload?.fundamentals && (!Array.isArray(newsPayload?.news) || newsPayload.news.length === 0)) {
        setInspectError("No provider data returned for this ticker.");
      }
    } catch {
      setInspectError("Unable to fetch provider lookup data.");
    } finally {
      setInspectLoading(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          <Stack
            direction={isMobile ? "column" : "row"}
            sx={{ alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between" }}
            mb={2}
            spacing={isMobile ? 2 : 0}
          >
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: 22, sm: 28 } }}>System Health</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: 14, sm: 16 } }}>
                Monitor provider health, anomaly status, and system metrics.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRefresh}
              disabled={loading}
              sx={{ width: isMobile ? "100%" : "auto", minHeight: 40 }}
            >
              Refresh
            </Button>
          </Stack>
          {loading ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                <CircularProgress />
              </Box>
              <Box sx={{ width: "100%", mt: 2 }}>
                <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={40} />
              </Box>
            </>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            providerHealth && Object.keys(providerHealth).length > 0 ? (
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <TableContainer component={Paper} sx={{ mt: 2, minWidth: 400 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Provider</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Role</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Status</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Message</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Sample Price</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Latency (s)</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Last Success</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Error Type</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Circuit</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Last Checked</TableCell>
                          <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>Next Check</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(providerHealth).map(([provider, row], idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>
                              <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                                {row.display_name || provider}
                                <HealthTrendIcon
                                  trend={(() => {
                                    const prev = prevHealth?.[provider]?.status;
                                    const curr = row.status;
                                    if (!prev) return "flat";
                                    if (statusRank[curr] > statusRank[prev]) return "up";
                                    if (statusRank[curr] < statusRank[prev]) return "down";
                                    return "flat";
                                  })()}
                                />
                                <Tooltip title="Refresh provider health" arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleProviderRefresh(provider)}
                                      disabled={!!rowLoading[provider]}
                                    >
                                      {rowLoading[provider] ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 12, sm: 14 }, color: "text.secondary", fontStyle: "italic" }}>
                              {PROVIDER_ROLES[provider] || "—"}
                            </TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>
                              <Chip
                                label={row.status === "ok" ? "OK" : row.status === "warn" ? "Warning" : "Error"}
                                size="small"
                                sx={{
                                  bgcolor:
                                    row.status === "ok"
                                      ? "#43a047"
                                      : row.status === "warn"
                                      ? "#ffb300"
                                      : "#e53935",
                                  color: "#fff"
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.message}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.sample_price}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.latency}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.last_success}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.error_type}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>
                              <Chip
                                label={isCircuitOpenRow(row) ? "OPEN" : "CLOSED"}
                                size="small"
                                color={isCircuitOpenRow(row) ? "warning" : "default"}
                                variant={isCircuitOpenRow(row) ? "outlined" : "filled"}
                              />
                            </TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.last_checked}</TableCell>
                            <TableCell sx={{ px: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 15 } }}>{row.next_check}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Provider Health History
                      </Typography>
                      {healthHistory.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No provider health snapshots recorded yet.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Providers</TableCell>
                                <TableCell>OK</TableCell>
                                <TableCell>Warn</TableCell>
                                <TableCell>Error</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {healthHistory.map((snapshot, idx) => {
                                const providers = snapshot?.providers || {};
                                const rows = Object.values(providers);
                                const okCount = rows.filter((r) => r?.status === "ok").length;
                                const warnCount = rows.filter((r) => r?.status === "warn").length;
                                const errorCount = rows.filter((r) => r?.status === "error").length;
                                return (
                                  <TableRow key={`${snapshot?.as_of || "history"}-${idx}`}>
                                    <TableCell>{snapshot?.as_of || ""}</TableCell>
                                    <TableCell>{rows.length}</TableCell>
                                    <TableCell>{okCount}</TableCell>
                                    <TableCell>{warnCount}</TableCell>
                                    <TableCell>{errorCount}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Provider Data Inspector
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Query normalized fundamentals/news payloads and inspect provider fallback errors.
                      </Typography>

                      <Box component="form" onSubmit={handleInspectProviderData} sx={{ mb: 1.5 }}>
                        <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ alignItems: "stretch" }}>
                          <TextField
                            label="Ticker"
                            size="small"
                            value={inspectTicker}
                            onChange={(e) => setInspectTicker(e.target.value)}
                            placeholder="AAPL"
                            fullWidth
                          />
                          <Button type="submit" variant="contained" disabled={inspectLoading}>
                            {inspectLoading ? "Loading..." : "Inspect"}
                          </Button>
                        </Stack>
                      </Box>

                      {inspectError && <Alert severity="warning" sx={{ mb: 1 }}>{inspectError}</Alert>}

                      {inspectFundamentals?.fundamentals && (
                        <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Fundamentals ({inspectFundamentals.provider || "unknown"})
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Company: {inspectFundamentals.fundamentals.company_name || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Sector: {inspectFundamentals.fundamentals.sector || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Industry: {inspectFundamentals.fundamentals.industry || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            P/E: {inspectFundamentals.fundamentals.pe_ratio ?? "--"}
                          </Typography>
                        </Paper>
                      )}

                      {inspectNews.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                            News ({inspectNews.length})
                          </Typography>
                          {inspectNews.map((item, index) => (
                            <Typography key={`${item.headline || "news"}-${index}`} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.35 }}>
                              {item.headline}
                            </Typography>
                          ))}
                        </Paper>
                      )}

                      {inspectErrors.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                            Provider Error Chain
                          </Typography>
                          {inspectErrors.map((row, idx) => (
                            <Typography key={`${row.provider || "provider"}-${idx}`} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.35 }}>
                              {(row.provider || "unknown").toUpperCase()}: {row.error_category || "UNKNOWN"} - {row.error || "No details"}
                            </Typography>
                          ))}
                        </Paper>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                <Box color="#888" mt={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <span role="img" aria-label="info">ℹ️</span> No provider health data available.<br />Try refreshing or check your connection.
                  </Typography>
                  <img src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f527.svg" alt="Health" width="64" height="64" style={{ opacity: 0.5 }} />
                </Box>
              )
            )}
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
}

export default HealthView;
